import React, { Fragment, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Trans } from "react-i18next"
import ReactCrop from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"

import {
    Box,
    Button,
    Divider,
    Grid,
    LinearProgress,
    useTheme,
    Typography,
    Autocomplete,
    TextField,
    createFilterOptions
} from "@mui/material"

import { styled } from '@mui/material/styles';

import {
    Upload as IconUpload,
    ArrowBack,
    Crop,
    Delete
} from "@mui/icons-material"

import apiClient from "../../../../services/api"
import store from "../../../../store/index"
import { toggleMessageOpen } from "../../../../store/actions/Message"
import {
    setIsFloorEdit,
    setStoreData,
    setIsSceneUpdated
} from "../../../../store/actions/Store"
import { ElementsTable } from "./SelectFloorPlanLayer"

export default function EditBackground({layers}) {
    const { t } = useTranslation()

    const theme = useTheme();

    const [progress, setProgress] = useState(0)
    const [uploadStarted, setUploadStarted] = useState(false)
    const [crop, setCrop] = useState()
    const [preview, setPreview] = useState(null)
    const [title, setTitle] = useState("")
    const [selected, setSelected] = useState(null);

    const storeData = useSelector(state => state.store.storeData)

    const isDXF = storeData && storeData.dxf && storeData.dxf.endsWith('.dxf');

    let timer = 0

    useEffect(() => {
        if(storeData) {
            setPreview(storeData.floor_image)
            setSelected(storeData.floor_plan.floor_plan_layer);
        }
    }, [storeData])

    function LinearProgressWithLabel(props) {
        return (
            <Box
                sx={{
                    paddingTop: "15px",
                    display: uploadStarted ? "flex" : "none",
                    alignItems: "center"
                }}
            >
                <Box sx={{ width: "100%", mr: 1 }}>
                    <LinearProgress {...props} />
                    <Box alignContent="center">{title}</Box>
                </Box>
            </Box>
        )
    }

    const openFileUpload = () => {
        const fileInput = document.getElementById('dxfFileUploadInput');

        if (fileInput) {
            fileInput.click();
        }
    }

    const clearFileUpload = () => {
        const fileInput = document.getElementById('dxfFileUploadInput');

        if (fileInput) {
            fileInput.value = '';
        }
    }

    const onSaveDxfFile = (event) => {
        if (!event.target.files || event.target.files.length < 1) {
            return store.dispatch(toggleMessageOpen("Upload failed, no file found to upload!", "error"));
        }
    
        setProgress(0);
        clearInterval(timer);
        setUploadStarted(true);
        setTitle("Uploading and converting file..");

        const formData = new FormData();

        formData.append("id", storeData.floor_plan.floor_plan_id);
        formData.append("file", event.target.files[0]);

        apiClient.post("/api/easy-floor/upload-file", formData).then(response => {
            setUploadStarted(false);
            clearInterval(timer);

            if(response.data.success) {
                setPreview(response.data.floor_image);

                let clone = storeData;
                clone.dxf = response.data.floor_plan.dxf;
                //clone.texture.width = (response.data.texture.width * response.data.floor_plan.scale) / 10;
                //clone.texture.depth = (response.data.texture.height * response.data.floor_plan.scale) / 10;

                store.dispatch(toggleMessageOpen("File uploaded and converted successfully", "success"));

                store.dispatch(setIsFloorEdit(false));
                store.dispatch(setStoreData(clone));
                store.dispatch(setIsSceneUpdated(true));
            } else {
                store.dispatch(toggleMessageOpen(response.data.message, 'error'));
            }

            clearFileUpload();
        })
        .catch(error => {
            clearInterval(timer);
            setUploadStarted(false);
            clearFileUpload();

            store.dispatch(toggleMessageOpen(error.response.data.message, "error"));
        });

        return true;
    }

    const handleDelete = () => {
        apiClient
            .post('/api/easy-floor/delete-dxf', {id: storeData.floor_plan.floor_plan_id})
            .then(response => {
                store.dispatch(setStoreData(response.data))
                store.dispatch(toggleMessageOpen("DXF deleted successfully", "success"))
            })
            .catch(error => {
                store.dispatch(toggleMessageOpen(error.response.data.message, "error"))
            })
    }
    
    const inputStyle = {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: theme.palette.border.primary,
            },
            '&:hover fieldset': {
                borderColor: theme.palette.border.secondary,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.border.secondary,
            }
        }
    };

    const onSaveFloorPlanLayer = () => {
        const postData = {id: storeData.floor_plan.floor_plan_id, layer: selected};

        apiClient.post("/api/easy-floor/set-floor-plan-layer", postData).then(response => {
            //window.location.reload(true);
            
            let clone = storeData
            clone.floor_plan.floor_plan_layer = response.data.floor_plan.floor_plan_layer;

            store.dispatch(toggleMessageOpen("Floor plan layer changed successfully", "success"))
            store.dispatch(setStoreData(clone))
            store.dispatch(setIsSceneUpdated(true))
            store.dispatch(setIsFloorEdit(false));
        })
        .catch(error => {
            store.dispatch(toggleMessageOpen(error.response.data.message, "error"))
        })
    }

    const LayerSelector = ({layers}) => {
        if(!layers) {
            return 'no layers';
        }

        return (
            <Grid container spacing={2} sx={{ padding: 2 }}>
                <Grid item xs={12} md={6}>
                    <Grid container spacing={2} alignItems='center'>
                        <Grid item>
                            <Typography variant="h2">
                                <Trans>Change floorPlan base layer</Trans>
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" onClick={onSaveFloorPlanLayer}>
                                <Trans>Change</Trans>
                            </Button>
                        </Grid>
                    </Grid>
                    <ElementsTable rows={layers} selected={selected} setSelected={setSelected} />
                </Grid>
            </Grid>
        )
    }

    return (
        <Fragment>
            <Grid
                container
                spacing={1}
                alignItems="center"
                sx={{padding: '5px'}}
            >
                <Grid item>
                    <Button
                        onClick={openFileUpload}
                        startIcon={<IconUpload />}
                        style={{ backgroundColor: theme.palette.background.button, color: '#FFF' }}
                        variant="contained"
                    >
                        {t("Upload")}
                    </Button>
                </Grid>
                <Grid item style={{display: isDXF ? 'block' : 'none' }}>
                    <Button
                        variant="contained"
                        startIcon={<Delete />}
                        onClick={handleDelete}
                        style={{ backgroundColor: theme.palette.background.button, color: '#FFF' }}
                    >
                        {t("Remove DXF")}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        startIcon={<ArrowBack />}
                        onClick={() => { store.dispatch(setIsFloorEdit(false)) }}
                        style={{ backgroundColor: theme.palette.background.button, color: '#FFF' }}
                    >
                        {t("Back To Map")}
                    </Button>
                </Grid>
            </Grid>

            <Box sx={{ width: "100%" }}>
                <LinearProgressWithLabel value={progress} />
            </Box>

            <Divider style={{ marginTop: "10px", marginBottom: "10px" }} />
            {
                isDXF
                ?   <LayerSelector layers={layers} />
                :   <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => { setCrop(percentCrop) }}
                        onComplete={(_, percentCrop) => {}}
                    >
                        {preview ? <img src={preview} alt="preview" /> : ""}
                    </ReactCrop>
            }

            <input type="file" id="dxfFileUploadInput" onChange={onSaveDxfFile} style={{ display: "none" }} />
        </Fragment>
    )
}
